/* ==========================================================================
   X-HEALTHY — header.js
   <sticky-header>     scroll state, compact mode, hide rows on scroll down,
                       exposes --header-height
   <mega-menu>         disclosure navigation (hover intent + click + keyboard)
   <menu-drawer>       sliding levels inside the mobile drawer
   <announcement-bar>  rotating messages, copy code, dismiss
   ========================================================================== */

(() => {
  const { theme } = window;

  /* ---------- <sticky-header> ---------- */
  if (!customElements.get('sticky-header')) {
    customElements.define(
      'sticky-header',
      class StickyHeader extends HTMLElement {
        connectedCallback() {
          this.hideOnScroll = this.dataset.hideOnScroll === 'true';
          this.lastY = window.scrollY;
          this.ticking = false;
          this.onScroll = this.onScroll.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });

          this.resizeObserver = new ResizeObserver(() => this.setHeight());
          this.resizeObserver.observe(this);
          this.setHeight();
          this.update();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          this.resizeObserver?.disconnect();
        }

        setHeight() {
          const rect = this.getBoundingClientRect();
          const root = document.documentElement.style;
          root.setProperty('--header-height', `${Math.round(rect.height)}px`);
          // Reserve the expanded height only while fully expanded at the top of the page
          if (!this.hasAttribute('data-scrolled') && !this.hasAttribute('data-collapsed')) {
            root.setProperty('--header-full-height', `${Math.round(rect.height)}px`);
          }
          const mobileSearch = this.querySelector('.header__search--mobile');
          if (mobileSearch && mobileSearch.offsetParent) {
            const bottom = mobileSearch.getBoundingClientRect().bottom;
            document.documentElement.style.setProperty('--search-panel-top', `${Math.round(bottom + 8)}px`);
          }
        }

        onScroll() {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.update();
            this.ticking = false;
          });
        }

        update() {
          const y = Math.max(0, window.scrollY);
          const delta = y - this.lastY;
          const before = this.hasAttribute('data-scrolled') + ':' + this.hasAttribute('data-collapsed');
          this.toggleAttribute('data-scrolled', y > 40);

          // Never collapse while search results or a mega panel are open
          const busy = this.querySelector('predictive-search[open], .mega-nav__link[aria-expanded="true"]');
          if (this.hideOnScroll && !busy && Math.abs(delta) > 6) {
            this.toggleAttribute('data-collapsed', delta > 0 && y > 160);
          }
          if (y < 160) this.removeAttribute('data-collapsed');
          this.lastY = y;
          // Measure only when the header state changes (size changes are caught by the ResizeObserver)
          const after = this.hasAttribute('data-scrolled') + ':' + this.hasAttribute('data-collapsed');
          if (after !== before) requestAnimationFrame(() => this.setHeight());
        }

        reveal() {
          this.removeAttribute('data-collapsed');
          this.lastY = window.scrollY;
        }
      }
    );
  }

  /* ---------- <mega-menu> ---------- */
  if (!customElements.get('mega-menu')) {
    customElements.define(
      'mega-menu',
      class MegaMenu extends HTMLElement {
        connectedCallback() {
          this.items = [...this.querySelectorAll('.mega-nav__item--has-panel')];
          this.backdrop = this.closest('sticky-header')?.querySelector('[data-mega-backdrop]');
          this.hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');

          this.items.forEach((item) => {
            const button = item.querySelector(':scope > button');
            button.addEventListener('click', () => (this.isOpen(item) ? this.close() : this.open(item)));
            item.addEventListener('mouseenter', () => {
              if (!this.hoverable.matches) return;
              clearTimeout(this.closeTimer);
              clearTimeout(this.openTimer);
              this.openTimer = setTimeout(() => this.open(item), this.current ? 0 : 140);
            });
            item.addEventListener('mouseleave', () => {
              if (!this.hoverable.matches) return;
              clearTimeout(this.openTimer);
              this.closeTimer = setTimeout(() => this.close(), 220);
            });
          });

          this.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.current) {
              const button = this.current.querySelector(':scope > button');
              this.close();
              button.focus();
            }
          });
          this.addEventListener('focusout', (event) => {
            if (this.current && !this.contains(event.relatedTarget)) this.close();
          });
          this.backdrop?.addEventListener('click', () => this.close());
          this.backdrop?.addEventListener('mouseenter', () => this.close());
        }

        isOpen(item) {
          return this.current === item;
        }

        open(item) {
          if (this.current === item) return;
          if (this.current) this.close(false);
          const button = item.querySelector(':scope > button');
          const panel = item.querySelector('.mega');
          button.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
          if (this.backdrop) this.backdrop.hidden = false;
          this.current = item;
        }

        close(hideBackdrop = true) {
          if (!this.current) return;
          this.current.querySelector(':scope > button').setAttribute('aria-expanded', 'false');
          this.current.querySelector('.mega').hidden = true;
          if (hideBackdrop && this.backdrop) this.backdrop.hidden = true;
          this.current = null;
        }
      }
    );
  }

  /* ---------- <menu-drawer> ---------- */
  if (!customElements.get('menu-drawer')) {
    customElements.define(
      'menu-drawer',
      class MenuDrawer extends HTMLElement {
        connectedCallback() {
          this.addEventListener('click', (event) => {
            const opener = event.target.closest('[data-open-panel]');
            if (opener) {
              this.show(opener.dataset.openPanel, opener);
              return;
            }
            if (event.target.closest('[data-close-panel]')) this.back();
          });
          this.closest('x-drawer')?.addEventListener('overlay:close', () => this.reset());
        }

        get active() {
          return this.querySelector('.menu-drawer__panel.is-active');
        }

        show(id, opener) {
          const panel = this.querySelector(`[data-panel="${id}"]`);
          if (!panel) return;
          const from = this.active;
          panel.dataset.returnTo = from.dataset.panel;
          panel.opener = opener;
          opener?.setAttribute('aria-expanded', 'true');
          this.swap(from, panel, 'slide-forward');
          panel.querySelector('[data-close-panel]')?.focus({ preventScroll: true });
        }

        back() {
          const from = this.active;
          const to = this.querySelector(`[data-panel="${from.dataset.returnTo || 'root'}"]`);
          if (!to) return;
          from.opener?.setAttribute('aria-expanded', 'false');
          this.swap(from, to, 'slide-back');
          (from.opener || to.querySelector('a, button'))?.focus({ preventScroll: true });
        }

        swap(from, to, animation) {
          from.classList.remove('is-active', 'slide-forward', 'slide-back');
          from.hidden = true;
          to.hidden = false;
          to.classList.add('is-active');
          to.classList.remove('slide-forward', 'slide-back');
          void to.offsetWidth;
          to.classList.add(animation);
          to.scrollTop = 0;
        }

        reset() {
          this.querySelectorAll('.menu-drawer__panel').forEach((panel) => {
            const root = panel.dataset.panel === 'root';
            panel.hidden = !root;
            panel.classList.toggle('is-active', root);
            panel.classList.remove('slide-forward', 'slide-back');
          });
          this.querySelectorAll('[data-open-panel]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
        }
      }
    );
  }

  /* ---------- <announcement-bar> ---------- */
  if (!customElements.get('announcement-bar')) {
    customElements.define(
      'announcement-bar',
      class AnnouncementBar extends HTMLElement {
        connectedCallback() {
          this.items = [...this.querySelectorAll('.announcement__item')];
          this.index = 0;

          this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.go(-1, true));
          this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.go(1, true));
          this.querySelector('[data-announcement-close]')?.addEventListener('click', () => this.dismiss());

          if (this.dataset.mode === 'marquee') {
            const track = this.querySelector('.announcement__track');
            const seconds = Math.max(20, Math.round(track.scrollWidth / 2 / 45));
            this.style.setProperty('--marquee-duration', `${seconds}s`);
          } else if (this.items.length > 1) {
            this.start();
            this.addEventListener('mouseenter', () => this.stop());
            this.addEventListener('mouseleave', () => this.start());
            this.addEventListener('focusin', () => this.stop());
            this.addEventListener('focusout', () => this.start());
          }
        }

        disconnectedCallback() {
          this.stop();
        }

        start() {
          const delay = Number(this.dataset.autoplay) || 5000;
          if (theme.utils.prefersReducedMotion()) return;
          this.stop();
          this.timer = setInterval(() => this.go(1), delay);
        }

        stop() {
          clearInterval(this.timer);
        }

        go(step, manual = false) {
          const current = this.items[this.index];
          this.index = (this.index + step + this.items.length) % this.items.length;
          const next = this.items[this.index];
          current.classList.remove('is-active');
          current.classList.add('is-leaving');
          current.setAttribute('aria-hidden', 'true');
          current.querySelectorAll('a, button').forEach((el) => (el.tabIndex = -1));
          setTimeout(() => current.classList.remove('is-leaving'), 320);
          next.classList.add('is-active');
          next.removeAttribute('aria-hidden');
          next.querySelectorAll('a, button').forEach((el) => el.removeAttribute('tabindex'));
          if (manual) this.start();
        }

        dismiss() {
          try {
            localStorage.setItem('xh-announcement-closed', this.dataset.version);
          } catch {
            /* ignore */
          }
          this.hidden = true;
        }
      }
    );
  }

  /* ---------- Global header behaviours ---------- */
  document.addEventListener('click', async (event) => {
    // Copy discount code
    const copy = event.target.closest('[data-copy]');
    if (copy) {
      try {
        await navigator.clipboard.writeText(copy.dataset.copy);
        theme.toast({ message: theme.strings.codeCopied.replace('[code]', copy.dataset.copy) });
      } catch {
        /* clipboard unavailable */
      }
      return;
    }

    // Cart icon: open the drawer when it exists, otherwise follow the link
    const cartTrigger = event.target.closest('[data-cart-trigger]');
    if (cartTrigger && theme.settings.cartType === 'drawer') {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(cartTrigger);
      }
      return;
    }

    // Bottom bar search: reveal the header and focus the field
    if (event.target.closest('[data-search-focus]')) {
      const header = document.querySelector('sticky-header');
      header?.reveal();
      const input = document.getElementById('Search-Mobile');
      if (input) {
        window.scrollTo({ top: 0, behavior: theme.utils.prefersReducedMotion() ? 'auto' : 'smooth' });
        input.focus({ preventScroll: true });
      }
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-auto-submit]')) event.target.form?.submit();
  });
})();
